import { createContext, useContext, type ReactElement, type ReactNode } from 'react';
import type { MeResponse } from '@citibox-panel/api-client';

export interface Session {
  permissions: readonly string[];
  roleIds: readonly string[];
  roleNames: readonly string[];
}

export function toSession(me: MeResponse): Session {
  return {
    permissions: me.permissions,
    // Verbatim, unknown ids included: only the spaces catalog gives an id meaning.
    roleIds: me.roles.map((role) => role.id),
    // A blank display name would leave an empty slot in the menu; the id at least names the role.
    roleNames: me.roles.map((role) => (role.name.trim() === '' ? role.id : role.name)),
  };
}

const SessionContext = createContext<Session | null>(null);

export function SessionProvider({ value, children }: { value: Session; children: ReactNode }): ReactElement {
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

// Only below a loaded /me: anything above it has no permissions to read and must not guess.
export function useSession(): Session {
  const session = useContext(SessionContext);
  if (session === null) {
    throw new Error('useSession must be used below a loaded session.');
  }
  return session;
}
